import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../services/api';
import { useAuth } from '../context/useAuth';
import TrainingBodyMeasurements from '../components/training/TrainingBodyMeasurements';
import CoachClientOverviewCard from '../components/coach/CoachClientOverviewCard';
import SportCoachDashboard from '../components/training/SportCoachDashboard';
import {
  formatYmdLocal,
  resolveHistoryRange,
  lastNDaysRange,
  groupEvolutionPointsByWeek,
} from '../utils/trainingHistoryRange';

function rolesOf(user) {
  return Array.isArray(user?.roles) && user.roles.length
    ? user.roles
    : [user?.rol].filter(Boolean);
}

const FOLLOW_CARD = 'card training-follow-card training-light-island surface-readable ui-light-panel bg-white border border-violet-100';

function isCoachActor(user) {
  const roles = rolesOf(user);
  return roles.some((r) => [
    'entrenador', 'nutricionista', 'admin_training', 'admin_entrenador',
    'admin_marca', 'admin_gimnasio', 'super_admin',
  ].includes(r));
}

function SeverityBadge({ severity }) {
  const cls = severity === 'high'
    ? 'bg-red-100 text-red-800 border-red-200'
    : severity === 'medium'
      ? 'bg-amber-100 text-amber-900 border-amber-200'
      : 'bg-stone-100 text-stone-700 border-stone-200';
  const label = severity === 'high' ? 'Alta' : severity === 'medium' ? 'Media' : 'Baja';
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cls}`}>
      {label}
    </span>
  );
}

const EVOLUTION_METRICS = [
  { id: 'weight_kg', label: 'Carga (kg)', short: 'Carga', color: '#0f766e', unit: 'kg' },
  { id: 'reps', label: 'Repeticiones', short: 'Reps', color: '#1d4ed8', unit: 'reps' },
  { id: 'intensity', label: 'RIR / RPE', short: 'RIR/RPE', color: '#b45309', unit: '' },
];

function formatMetricValue(metricId, value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  if (metricId === 'intensity') return String(n);
  if (metricId === 'reps') return Number.isInteger(n) ? String(n) : n.toFixed(1);
  return `${n} kg`;
}

function normalizeEvolutionPayload(data) {
  if (Array.isArray(data)) {
    // Compat: formato antiguo (promedio de sesión). Se trata como un solo “serie mixta”.
    return data.length
      ? [{ exercise_name: 'Sesión (promedio)', points: data, delta_kg: 0, trend: 'flat' }]
      : [];
  }
  if (data && Array.isArray(data.exercises)) return data.exercises;
  return [];
}

/**
 * Gráfico de evolución por ejercicio (carga / reps / RIR-RPE).
 * Agrupa por día o por semana; no mezcla cargas de distintos ejercicios.
 */
function CoachEvolutionChart({ data, rangeLabel, onApplyPeriod }) {
  const exercises = useMemo(() => normalizeEvolutionPayload(data), [data]);
  const [metricId, setMetricId] = useState('weight_kg');
  const [groupBy, setGroupBy] = useState('day'); // day | week
  const [exerciseName, setExerciseName] = useState('');

  useEffect(() => {
    if (!exercises.length) {
      setExerciseName('');
      return;
    }
    const stillValid = exercises.some((ex) => ex.exercise_name === exerciseName);
    if (!stillValid) {
      const preferred = exercises.find((ex) => ex.from_plan) || exercises[0];
      setExerciseName(preferred.exercise_name);
    }
  }, [exercises, exerciseName]);

  const selected = exercises.find((ex) => ex.exercise_name === exerciseName) || exercises[0] || null;
  const metric = EVOLUTION_METRICS.find((m) => m.id === metricId) || EVOLUTION_METRICS[0];

  const chartData = useMemo(() => {
    const raw = selected?.points || [];
    const points = groupBy === 'week' ? groupEvolutionPointsByWeek(raw) : raw;
    return points.map((row) => ({
      ...row,
      weight_kg: row.weight_kg != null ? Number(row.weight_kg) : null,
      reps: row.reps != null ? Number(row.reps) : null,
      intensity: row.intensity != null ? Number(row.intensity) : null,
    }));
  }, [selected, groupBy]);

  const hasAny = chartData.some((r) => (
    (r.weight_kg != null && r.weight_kg > 0)
    || (r.reps != null && r.reps > 0)
    || (r.intensity != null && r.intensity >= 0)
  ));

  const applyPresetDays = (days) => {
    if (!onApplyPeriod) return;
    const r = lastNDaysRange(days);
    onApplyPeriod({ mode: 'range', from: r.from, to: r.to });
  };

  return (
    <div className={`${FOLLOW_CARD} p-4 mb-4 overflow-hidden`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
        <div>
          <h4 className="font-bold text-stone-900 m-0">Evolución del asesorado</h4>
          <p className="text-xs text-stone-600 m-0 mt-1">
            Carga, reps y RIR/RPE por ejercicio · {rangeLabel || 'rango activo'}
          </p>
        </div>
        <div className="grid grid-cols-3 sm:flex gap-1.5">
          {EVOLUTION_METRICS.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`${metricId === m.id ? 'btn-primary text-xs' : 'btn-secondary text-xs'} w-full sm:w-auto`}
              onClick={() => setMetricId(m.id)}
            >
              {m.short}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <label className="text-xs text-stone-600">
          Ejercicio
          <select
            className="input mt-1 block w-full text-sm"
            value={exerciseName}
            onChange={(e) => setExerciseName(e.target.value)}
            disabled={!exercises.length}
          >
            {!exercises.length ? (
              <option value="">Sin ejercicios</option>
            ) : (
              exercises.map((ex) => (
                <option key={ex.exercise_name} value={ex.exercise_name}>
                  {ex.from_plan ? '★ ' : ''}
                  {ex.exercise_name}
                  {ex.points?.length
                    ? (ex.delta_kg != null && Number(ex.delta_kg) !== 0
                      ? ` (${ex.delta_kg > 0 ? '+' : ''}${ex.delta_kg} kg)`
                      : '')
                    : ' (rutina · sin logs)'}
                </option>
              ))
            )}
          </select>
        </label>
        <div>
          <p className="text-xs text-stone-600 m-0 mb-1">Agrupar</p>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              className={`${groupBy === 'day' ? 'btn-primary text-xs' : 'btn-secondary text-xs'} w-full`}
              onClick={() => setGroupBy('day')}
            >
              Por día
            </button>
            <button
              type="button"
              className={`${groupBy === 'week' ? 'btn-primary text-xs' : 'btn-secondary text-xs'} w-full`}
              onClick={() => setGroupBy('week')}
            >
              Por semana
            </button>
          </div>
        </div>
      </div>

      {onApplyPeriod && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <span className="text-[11px] text-stone-500 self-center mr-1">Periodo rápido:</span>
          {[
            { label: '4 sem', days: 28 },
            { label: '8 sem', days: 56 },
            { label: '12 sem', days: 84 },
            { label: '30 días', days: 30 },
          ].map((p) => (
            <button
              key={p.label}
              type="button"
              className="btn-secondary text-xs"
              onClick={() => applyPresetDays(p.days)}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => onApplyPeriod({ mode: 'all' })}
          >
            Todo
          </button>
        </div>
      )}

      {selected && (
        <p className="text-xs text-stone-600 m-0 mb-2">
          <strong className="text-stone-800">{selected.exercise_name}</strong>
          {selected.first_weight != null && selected.last_weight != null ? (
            <> · {selected.first_weight} kg → {selected.last_weight} kg</>
          ) : null}
          {selected.delta_kg != null && Number(selected.delta_kg) !== 0 ? (
            <span className={selected.trend === 'up' ? ' text-lime-700 font-semibold' : selected.trend === 'down' ? ' text-red-600 font-semibold' : ''}>
              {' '}({selected.delta_kg > 0 ? '+' : ''}{selected.delta_kg} kg)
            </span>
          ) : null}
          {' · '}
          {groupBy === 'week' ? 'promedio semanal del ejercicio' : 'valor del ejercicio por sesión'}
        </p>
      )}

      {!hasAny ? (
        <p className="text-sm text-stone-500 m-0 border border-dashed border-stone-300 rounded-xl p-6 text-center">
          Sin registros de carga, reps o intensidad en este rango. Cuando el asesorado guarde series por ejercicio, verás la evolución aquí.
        </p>
      ) : (
        <>
          <ChartBox heightClass="h-52 sm:h-56">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#57534e' }} interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 11, fill: '#57534e' }}
                  width={36}
                  allowDecimals={metricId !== 'reps'}
                />
                <Tooltip
                  formatter={(value) => [formatMetricValue(metricId, value), metric.label]}
                  labelFormatter={(_, payload) => {
                    const row = payload?.[0]?.payload;
                    const name = selected?.exercise_name || '';
                    return row?.fecha ? `${name} · ${row.fecha}` : name;
                  }}
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e7e5e4',
                    borderRadius: 8,
                    color: '#1c1917',
                    fontSize: 12,
                  }}
                />
                <Bar
                  dataKey={metric.id}
                  name={metric.label}
                  fill={metric.color}
                  radius={[6, 6, 0, 0]}
                  maxBarSize={48}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartBox>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {EVOLUTION_METRICS.map((m) => {
              const mini = chartData.filter((r) => r[m.id] != null);
              return (
                <div key={m.id} className="rounded-xl border border-stone-200 bg-slate-50 p-2.5 overflow-hidden">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-stone-600 m-0 mb-1">{m.label}</p>
                  <ChartBox heightClass="h-24">
                    <ResponsiveContainer width="100%" height="100%" minHeight={96}>
                      <BarChart data={mini.length ? chartData : []} margin={{ top: 4, right: 2, left: -18, bottom: 0 }}>
                        <XAxis dataKey="label" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip
                          formatter={(value) => [formatMetricValue(m.id, value), m.label]}
                          contentStyle={{
                            background: '#fff',
                            border: '1px solid #e7e5e4',
                            borderRadius: 8,
                            fontSize: 11,
                          }}
                        />
                        <Bar dataKey={m.id} fill={m.color} radius={[4, 4, 0, 0]} maxBarSize={28} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartBox>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function ProgressionBars({ points, exerciseName }) {
  const slice = (points || []).slice(-12);
  if (!slice.length) return null;
  const maxW = Math.max(1, ...slice.map((pt) => Math.max(0, Number(pt.weight_kg) || 0)));
  const barMaxPx = 56;
  return (
    <div className="mt-2 h-14 flex items-end gap-1 overflow-hidden" aria-hidden>
      {slice.map((pt, i) => {
        const w = Math.max(0, Number(pt.weight_kg) || 0);
        const h = Math.max(4, Math.round((w / maxW) * barMaxPx));
        const reps = pt.reps_avg != null ? ` · ${pt.reps_avg} reps` : (pt.reps ? ` · ${pt.reps}` : '');
        const intensity = pt.intensity != null ? ` · RIR/RPE ${pt.intensity}` : '';
        return (
          <div
            key={`${exerciseName}-${i}`}
            className="flex-1 min-w-[4px] max-w-[2rem] rounded-t bg-lime-500"
            style={{ height: `${h}px` }}
            title={`${pt.fecha}: ${w} kg${reps}${intensity}`}
          />
        );
      })}
    </div>
  );
}

function ChartBox({ heightClass = 'h-40', children }) {
  return (
    <div className={`${heightClass} w-full min-h-0 overflow-hidden`}>
      {children}
    </div>
  );
}
function Heatmap({ data }) {
  // data: [{date, intensity, completed, total}]
  if (!Array.isArray(data) || !data.length) return null;
  const color = (i) => (i === 0 ? 'bg-stone-100' : i === 1 ? 'bg-lime-200' : i === 2 ? 'bg-lime-400' : 'bg-lime-600');
  return (
    <div className="grid grid-cols-7 gap-1">
      {data.map((d) => (
        <div
          key={d.date}
          className={`h-5 rounded ${color(d.intensity)} border border-stone-200`}
          title={`${d.date}: ${d.completed}/${d.total} sesiones`}
        />
      ))}
    </div>
  );
}

export default function TrainingExpertProgress({ onBack = null, initialTab = 'resumen', analyticsOnly = false, onNavigate = null }) {
  const { user } = useAuth();
  const coachMode = isCoachActor(user);
  const [clients, setClients] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [insights, setInsights] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [error, setError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  /** week | range — historial del asesorado */
  const [historyMode, setHistoryMode] = useState('week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');

  const historyRange = useMemo(
    () => resolveHistoryRange(historyMode, { weekOffset, rangeFrom, rangeTo }),
    [historyMode, weekOffset, rangeFrom, rangeTo],
  );

  const loadCoachData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [clientsRes, lightRes] = await Promise.all([
        api.get('/training/coach/clients'),
        api.get('/training/progress/traffic-light/clients').catch(() => ({ data: { data: [] } })),
      ]);
      const list = clientsRes.data?.data || [];
      const lights = lightRes.data?.data || [];
      const lightById = new Map(lights.map((l) => [Number(l.user_id), l]));
      const merged = list.map((c) => {
        const light = lightById.get(Number(c.id));
        return {
          ...c,
          traffic_light: light?.traffic_light || 'red',
          adherence_pct: light?.adherence_pct ?? c.adherence ?? 0,
        };
      });
      setClients(merged);
      setSelectedUserId((prev) => prev || (merged[0] ? String(merged[0].id) : ''));
      try {
        const notifRes = await api.get('/training/coach/notifications', { params: { unread: 'true' } });
        setNotifications(notifRes.data?.data || []);
      } catch {
        setNotifications([]);
      }
    } catch {
      setError('No se pudo cargar el panel de seguimiento.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInsights = useCallback(async () => {
    if (!selectedUserId) {
      setInsights(null);
      setLogs([]);
      return;
    }
    try {
      const params = { user_id: selectedUserId };
      const insightParams = {};
      if (historyRange.from) insightParams.from = historyRange.from;
      if (historyRange.to) insightParams.to = historyRange.to;
      const [insRes, logRes] = await Promise.all([
        api.get(`/training/coach/clients/${selectedUserId}/insights`, { params: insightParams }),
        api.get('/training/admin/log', { params }),
      ]);
      setInsights(insRes.data?.data || null);
      const allLogs = logRes.data?.data || [];
      const from = historyRange.from;
      const to = historyRange.to;
      const filtered = (from || to)
        ? allLogs.filter((l) => {
          const f = String(l?.fecha || '').slice(0, 10);
          if (from && f < from) return false;
          if (to && f > to) return false;
          return true;
        })
        : allLogs;
      setLogs(filtered);
    } catch {
      setError('No se pudo cargar la ficha del entrenado.');
    }
  }, [selectedUserId, historyRange.from, historyRange.to]);

  useEffect(() => {
    if (coachMode) loadCoachData();
    else loadAthleteLogs();
  }, [coachMode, loadCoachData]);

  useEffect(() => {
    if (!coachMode) return;
    try {
      const saved = sessionStorage.getItem('coach.tracking.selectedUserId');
      if (saved && clients.some((c) => String(c.id) === String(saved))) {
        setSelectedUserId(saved);
      }
    } catch {
      /* ignore */
    }
  }, [coachMode, clients]);

  useEffect(() => {
    if (!coachMode || !selectedUserId) return;
    try {
      sessionStorage.setItem('coach.tracking.selectedUserId', String(selectedUserId));
    } catch {
      /* ignore */
    }
  }, [coachMode, selectedUserId]);

  const loadAthleteLogs = async () => {
    setLoading(true);
    try {
      const resp = await api.get('/training/admin/log');
      setLogs(resp.data?.data || []);
    } catch {
      setError('No se pudo cargar el diario.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (coachMode && selectedUserId) loadInsights();
  }, [coachMode, selectedUserId, loadInsights]);

  const kpis = useMemo(() => {
    if (coachMode && insights) {
      const s = insights.summary || {};
      return {
        sessions: s.totalSessions || 0,
        completed: s.completedSessions || 0,
        adherence: s.totalSessions
          ? Math.round((s.completedSessions / s.totalSessions) * 100)
          : 0,
        totalMin: s.totalMinutes || 0,
      };
    }
    const completed = logs.filter((l) => l.completado).length;
    const totalMin = logs.reduce((s, l) => s + (l.duration_minutes || 0), 0);
    return {
      sessions: logs.length,
      completed,
      adherence: logs.length ? Math.round((completed / logs.length) * 100) : 0,
      totalMin,
    };
  }, [coachMode, insights, logs]);

  const coachDashboard = useMemo(() => {
    if (!coachMode || !insights) return null;
    const k = insights.coach_kpis || {};
    const w7 = k.window_7d || {};
    const w14 = k.window_14d || {};
    const w30 = k.window_30d || {};
    const wr = k.window_range || null;
    return {
      adherence7: w7.adherence ?? 0,
      sessions7: w7.sessions ?? 0,
      adherence14: w14.adherence ?? 0,
      adherence30: w30.adherence ?? 0,
      rangeAdherence: wr?.adherence ?? null,
      rangeSessions: wr?.sessions ?? null,
      rangeCompleted: wr?.completed ?? null,
      streak: k.streak?.current ?? 0,
      bestStreak: k.streak?.best ?? 0,
      weightDelta4w: k.weight_delta_4w,
      prs: insights.prs || [],
      volumeChart: insights.volume_chart || [],
      evolutionChart: insights.evolution_chart || [],
    };
  }, [coachMode, insights]);

  const markAllNotifsRead = async () => {
    await api.post('/training/coach/notifications/read', {});
    setNotifications([]);
  };

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    const list = [...clients];
    const score = (c) => {
      let s = 0;
      if (!c.has_plan) s += 3;
      if ((c.unread_notifications || 0) > 0) s += 2;
      const days = c.last_session?.fecha
        ? Math.floor((Date.now() - new Date(c.last_session.fecha).getTime()) / 86400000)
        : 999;
      if (days >= 7) s += 3;
      if ((c.adherence || 0) < 50) s += 2;
      return s;
    };
    const out = q
      ? list.filter((c) => String(c.nombre || '').toLowerCase().includes(q) || String(c.email || '').toLowerCase().includes(q))
      : list;
    out.sort((a, b) => score(b) - score(a));
    return out.map((c) => ({ ...c, attention_score: score(c) }));
  }, [clients, clientSearch]);

  const clientsNeedingAttention = useMemo(
    () => filteredClients.filter((c) => c.attention_score >= 3),
    [filteredClients],
  );
  const activeClients = useMemo(
    () => filteredClients.filter((c) => c.attention_score < 3),
    [filteredClients],
  );

  const trafficDotClass = (light) => {
    const key = String(light || '').toLowerCase();
    if (key === 'green') return 'bg-emerald-500';
    if (key === 'yellow') return 'bg-amber-400';
    return 'bg-red-500';
  };

  const renderClientButton = (c) => {
    const selected = String(selectedUserId) === String(c.id);
    const light = String(c.traffic_light || 'red').toLowerCase();
    const adherence = c.adherence_pct ?? c.adherence ?? 0;
    return (
    <button
      key={c.id}
      type="button"
      onClick={() => setSelectedUserId(String(c.id))}
      className={`training-client-pick w-full text-left rounded-xl border p-3 text-sm transition-colors ${
        selected
          ? 'training-client-pick--selected border-lime-500 bg-lime-50'
          : 'border-stone-200 bg-white hover:bg-stone-50'
      }`}
    >
      <div className="flex items-center gap-2 font-semibold text-stone-900">
        <span
          className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${trafficDotClass(light)}`}
          title={`Semáforo: ${light}`}
          aria-label={`Semáforo ${light}`}
        />
        <span className="truncate">{c.nombre}</span>
        {c.soy_mama_posparto ? (
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded-full">
            Posparto
          </span>
        ) : null}
      </div>
      <div className="text-xs text-stone-600 mt-1">
        {c.has_plan ? 'Con plan' : 'Sin plan'} · {adherence}% adherencia
      </div>
      {c.unread_notifications > 0 && (
        <span className="inline-block mt-1 text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">
          {c.unread_notifications} nueva(s)
        </span>
      )}
    </button>
    );
  };

  return (
    <div className="dashboard-main-view training-coach-view">
      <header className="dashboard-header page-header live-classes-panel__header">
        <div className="flex flex-wrap justify-between gap-3 w-full">
          <div>
            {onBack && (
              <button type="button" className="btn-secondary mb-2" onClick={onBack}>
                ← Volver
              </button>
            )}
            <h1 className="d28d-page-title">{analyticsOnly ? 'Estadísticas' : 'Seguimiento'}</h1>
            <p className="d28d-text-muted subtitle">
              {analyticsOnly
                ? 'Panel analítico: adherencia, progresión, bienestar y evolución. Operación (planes, IA, galería) desde el menú o Panel Entrenadores.'
                : coachMode
                  ? 'Alertas prioritarias y ficha por cliente. Rutinas e IA desde Planes o Panel Entrenadores.'
                  : 'Tu historial de entrenamiento.'}
            </p>
          </div>
          {coachMode && !analyticsOnly && notifications.length > 0 && (
            <button type="button" className="btn-secondary text-sm" onClick={markAllNotifsRead}>
              Marcar {notifications.length} alertas leídas
            </button>
          )}
        </div>
      </header>

      {coachMode && notifications.length > 0 && (
        <div className="mb-4 space-y-2 training-light-island surface-readable training-follow-alerts rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-bold uppercase tracking-wide text-violet-950">Alertas</h3>
          {notifications.slice(0, 5).map((n) => (
            <div key={n.id} className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-sm text-violet-950">
              <strong className="text-violet-950">{n.title}</strong>
              <span className="text-violet-900"> — {n.body}</span>
              <span className="text-violet-900 text-xs ml-2">{new Date(n.created_at).toLocaleString('es')}</span>
            </div>
          ))}
        </div>
      )}

      {coachMode && (
        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          <aside className={`${FOLLOW_CARD} p-4 space-y-4 max-h-[75vh] overflow-y-auto`}>
            <input
              type="search"
              className="input w-full text-sm"
              placeholder="Buscar por nombre o email…"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
            />
            {loading && <p className="text-sm text-stone-500">Cargando…</p>}
            {clientsNeedingAttention.length > 0 ? (
              <div className="space-y-2">
                <h3 className="font-bold text-stone-900 text-xs uppercase tracking-wide text-amber-800">
                  Requieren atención ({clientsNeedingAttention.length})
                </h3>
                {clientsNeedingAttention.map(renderClientButton)}
              </div>
            ) : null}
            <div className="space-y-2">
              <h3 className="font-bold text-stone-900 text-xs uppercase tracking-wide">
                Clientes activos ({activeClients.length})
              </h3>
              {activeClients.length ? activeClients.map(renderClientButton) : (
                <p className="text-xs text-stone-500">Sin clientes activos en este momento.</p>
              )}
            </div>
          </aside>

          <section className="training-light-island surface-readable rounded-xl border border-slate-200 p-4 min-h-[12rem] overflow-hidden">
            {error && <p className="training-alert--error text-sm mb-3">{error}</p>}
            {!insights && !loading && (
              <p className="text-stone-600 p-8 border border-dashed border-slate-300 rounded-xl text-center">Selecciona un entrenado.</p>
            )}
            {insights && (
              <>
                <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-bold text-stone-800 m-0">Historial del asesorado</p>
                    <p className="text-xs text-stone-600 m-0">{historyRange.label}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
                    <button
                      type="button"
                      className={`${historyMode === 'week' ? 'btn-primary text-xs' : 'btn-secondary text-xs'} w-full sm:w-auto`}
                      onClick={() => setHistoryMode('week')}
                    >
                      Por semana
                    </button>
                    <button
                      type="button"
                      className={`${historyMode === 'range' ? 'btn-primary text-xs' : 'btn-secondary text-xs'} w-full sm:w-auto`}
                      onClick={() => {
                        setHistoryMode('range');
                        if (!rangeFrom && !rangeTo) {
                          const end = new Date();
                          const start = new Date();
                          start.setDate(start.getDate() - 29);
                          setRangeFrom(formatYmdLocal(start));
                          setRangeTo(formatYmdLocal(end));
                        }
                      }}
                    >
                      Fechas específicas
                    </button>
                    <button
                      type="button"
                      className={`${historyMode === 'all' ? 'btn-primary text-xs' : 'btn-secondary text-xs'} w-full sm:w-auto`}
                      onClick={() => setHistoryMode('all')}
                    >
                      Todo el historial
                    </button>
                  </div>
                  {historyMode === 'week' ? (
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
                      <button
                        type="button"
                        className="btn-secondary text-xs w-full sm:w-auto"
                        onClick={() => setWeekOffset((o) => o - 1)}
                      >
                        ← Semana anterior
                      </button>
                      <span className="text-sm font-semibold text-stone-800 sm:min-w-[10rem] text-center order-first sm:order-none">
                        {historyRange.label}
                      </span>
                      <button
                        type="button"
                        className="btn-secondary text-xs w-full sm:w-auto"
                        disabled={weekOffset >= 0}
                        onClick={() => setWeekOffset((o) => Math.min(0, o + 1))}
                      >
                        Semana siguiente →
                      </button>
                      {weekOffset !== 0 && (
                        <button
                          type="button"
                          className="btn-secondary text-xs w-full sm:w-auto"
                          onClick={() => setWeekOffset(0)}
                        >
                          Esta semana
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:flex sm:flex-wrap sm:items-end gap-3">
                      <label className="text-xs text-stone-600 w-full sm:w-auto">
                        Desde
                        <input
                          type="date"
                          className="input mt-1 block text-sm w-full"
                          value={rangeFrom}
                          max={rangeTo || undefined}
                          onChange={(e) => setRangeFrom(e.target.value)}
                        />
                      </label>
                      <label className="text-xs text-stone-600 w-full sm:w-auto">
                        Hasta
                        <input
                          type="date"
                          className="input mt-1 block text-sm w-full"
                          value={rangeTo}
                          min={rangeFrom || undefined}
                          onChange={(e) => setRangeTo(e.target.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn-secondary text-xs w-full sm:w-auto"
                        onClick={() => {
                          const end = new Date();
                          const start = new Date();
                          start.setDate(start.getDate() - 29);
                          setRangeFrom(formatYmdLocal(start));
                          setRangeTo(formatYmdLocal(end));
                        }}
                      >
                        Últimos 30 días
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 mb-4">
                  <button type="button" className="btn-primary text-xs w-full sm:w-auto" onClick={() => onNavigate?.('admintraining')}>
                    Asignar rutina
                  </button>
                  <button type="button" className="btn-secondary text-xs w-full sm:w-auto" onClick={() => setTab('progresion')}>
                    Ver cargas
                  </button>
                  <button type="button" className="btn-secondary text-xs w-full sm:w-auto" onClick={() => setTab('sesiones')}>
                    Historial sesiones
                  </button>
                </div>

                {(insights.alerts || []).length > 0 && (
                  <div className={`${FOLLOW_CARD} p-4 mb-4 border-amber-200`}>
                    <h4 className="font-bold mb-2 text-violet-950">Alertas del cliente</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {(insights.alerts || []).slice(0, 6).map((a) => (
                        <div key={a.code} className="bg-white rounded-xl border border-stone-200 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-semibold text-stone-900 text-sm">{a.title}</div>
                            <SeverityBadge severity={a.severity} />
                          </div>
                          <p className="text-xs text-stone-600 mt-1">{a.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {coachDashboard && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    {coachDashboard.rangeSessions != null ? (
                      <>
                        <Kpi label="Adherencia (filtro)" value={`${coachDashboard.rangeAdherence}%`} hint={historyRange.label} />
                        <Kpi label="Sesiones (filtro)" value={coachDashboard.rangeSessions} hint={`${coachDashboard.rangeCompleted || 0} completadas`} />
                      </>
                    ) : (
                      <>
                        <Kpi label="Adherencia 7d" value={`${coachDashboard.adherence7}%`} hint="Cumplimiento semanal" />
                        <Kpi label="Sesiones 7d" value={coachDashboard.sessions7} />
                      </>
                    )}
                    <Kpi label="Racha" value={`${coachDashboard.streak}d`} />
                    <Kpi label="Δ peso 4w" value={coachDashboard.weightDelta4w != null ? `${coachDashboard.weightDelta4w}kg` : '—'} />
                  </div>
                )}

                <CoachEvolutionChart
                  data={insights.evolution_chart || coachDashboard?.evolutionChart || { exercises: [] }}
                  rangeLabel={historyRange.label}
                  onApplyPeriod={({ mode, from, to }) => {
                    if (mode === 'all') {
                      setHistoryMode('all');
                      return;
                    }
                    setHistoryMode('range');
                    if (from) setRangeFrom(from);
                    if (to) setRangeTo(to);
                    setWeekOffset(0);
                  }}
                />

                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-4">
                  {['resumen', 'medidas', 'progresion', 'bienestar', 'deportes', 'sesiones'].map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`${tab === t ? 'btn-primary text-xs' : 'btn-secondary text-xs'} w-full sm:w-auto`}
                      onClick={() => setTab(t)}
                    >
                      {t === 'resumen' && 'Resumen'}
                      {t === 'medidas' && 'Medidas'}
                      {t === 'progresion' && 'Cargas'}
                      {t === 'bienestar' && 'Bienestar'}
                      {t === 'deportes' && 'Deportes'}
                      {t === 'sesiones' && 'Sesiones'}
                    </button>
                  ))}
                </div>

                {tab === 'resumen' && (
                  <>
                  {selectedUserId ? (
                    <div className="mb-4 space-y-4">
                      <CoachClientOverviewCard userId={selectedUserId} />
                    </div>
                  ) : null}
                  <div className="grid lg:grid-cols-3 gap-4">
                    <div className={`${FOLLOW_CARD} p-4`}>
                      <h4 className="font-bold mb-2 text-violet-950">Perfil</h4>
                      <ul className="text-sm text-violet-900 space-y-1">
                        <li><strong>Email:</strong> {insights.user.email}</li>
                        {insights.user.soy_mama_posparto ? (
                          <li>
                            <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wide bg-violet-100 text-violet-800 px-2 py-0.5 rounded-full">
                              Mamá posparto
                            </span>
                          </li>
                        ) : null}
                        <li><strong>Objetivo:</strong> {insights.user.objetivo || '—'}</li>
                        <li><strong>Última sesión:</strong> {insights.summary?.lastSession?.fecha || 'Sin registros'}</li>
                        {insights.days_since_last_session != null && (
                          <li className={insights.days_since_last_session > 6 ? 'text-red-700 font-semibold' : insights.days_since_last_session > 3 ? 'text-amber-700 font-semibold' : ''}>
                            {insights.days_since_last_session === 0 ? 'Entrenó hoy' : `Sin entrenar hace ${insights.days_since_last_session} días`}
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className={`${FOLLOW_CARD} p-4`}>
                      <h4 className="font-bold mb-2 text-violet-950">Plan activo</h4>
                      {insights.plan ? (
                        <div className="text-sm text-violet-900 space-y-1">
                          <div><strong>Método:</strong> {insights.plan.method}</div>
                          <div><strong>Nivel:</strong> {insights.plan.level}</div>
                          <div><strong>Días:</strong> {insights.plan.dias_count}</div>
                        </div>
                      ) : (
                        <p className="text-sm text-stone-500">Sin plan asignado.</p>
                      )}
                    </div>
                    <div className={`${FOLLOW_CARD} p-4`}>
                      <h4 className="font-bold mb-2 text-violet-950">Logros (PRs)</h4>
                      {(insights.prs || []).length === 0 ? (
                        <p className="text-sm text-violet-800">Sin PRs aún (falta registrar cargas/reps).</p>
                      ) : (
                        <ul className="text-xs text-violet-900 space-y-1 max-h-44 overflow-y-auto pr-1">
                          {(insights.prs || []).slice(0, 8).map((p) => (
                            <li key={p.exercise_name}>
                              <strong>{p.exercise_name}</strong>
                              {p.max_weight ? ` · ${p.max_weight}kg` : ''}
                              {p.max_reps ? ` · ${p.max_reps} reps` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {(insights.heatmap || []).length > 0 && (
                      <div className={`${FOLLOW_CARD} p-4 lg:col-span-2`}>
                        <h4 className="font-bold mb-2 text-violet-950">Calendario de sesiones (35 días)</h4>
                        <Heatmap data={insights.heatmap} />
                        <p className="text-xs text-violet-800 mt-2">Más oscuro = más cumplimiento ese día.</p>
                      </div>
                    )}
                    {(insights.improvements || []).length > 0 && (
                      <div className={`${FOLLOW_CARD} p-4`}>
                        <h4 className="font-bold mb-2 text-violet-950">Top mejoras (últimas 4 semanas)</h4>
                        <ul className="text-xs text-violet-900 space-y-1 max-h-44 overflow-y-auto pr-1">
                          {(insights.improvements || []).slice(0, 8).map((im) => (
                            <li key={im.exercise_name}>
                              <strong>{im.exercise_name}</strong>
                              {im.delta_kg ? ` · ${im.delta_kg > 0 ? '+' : ''}${im.delta_kg}kg` : ''}
                              {im.delta_reps ? ` · ${im.delta_reps > 0 ? '+' : ''}${im.delta_reps} reps` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(coachDashboard?.volumeChart || []).length > 1 && (
                      <div className={`${FOLLOW_CARD} p-4 lg:col-span-3 overflow-hidden`}>
                        <h4 className="font-bold mb-2 text-violet-950">Volumen semanal (tendencia)</h4>
                        <ChartBox heightClass="h-40">
                          <ResponsiveContainer width="100%" height="100%" minHeight={160}>
                            <LineChart data={coachDashboard.volumeChart}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#64748b' }} />
                              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} width={36} />
                              <Tooltip />
                              <Line type="monotone" dataKey="volume" stroke="#65a30d" strokeWidth={2} name="Volumen" dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </ChartBox>
                      </div>
                    )}
                  </div>
                  </>
                )}

                {tab === 'medidas' && (
                  <div className="space-y-4">
                    {(insights.measurement_chart || []).length > 1 && (
                      <div className={`${FOLLOW_CARD} p-4 overflow-hidden`}>
                        <h4 className="font-bold mb-2 text-violet-950">Evolución peso / cintura</h4>
                        <ChartBox heightClass="h-44">
                          <ResponsiveContainer width="100%" height="100%" minHeight={176}>
                            <LineChart data={insights.measurement_chart}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: '#64748b' }} />
                              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} width={36} />
                              <Tooltip />
                              <Line type="monotone" dataKey="weight_kg" stroke="#65a30d" strokeWidth={2} name="Peso" dot={false} />
                              <Line type="monotone" dataKey="waist_cm" stroke="#2563eb" strokeWidth={2} name="Cintura" dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </ChartBox>
                      </div>
                    )}
                    <TrainingBodyMeasurements readOnlyUserId={selectedUserId} />
                  </div>
                )}

                {tab === 'progresion' && (
                  <div className="space-y-3 overflow-hidden">
                    <p className="text-xs text-stone-600 m-0">
                      Detalle por ejercicio en el rango · el gráfico de barras superior resume carga, reps y RIR/RPE.
                    </p>
                    {(insights.progression || []).length === 0 && (
                      <p className="text-stone-500 text-sm">Aún no hay datos de cargas registradas.</p>
                    )}
                    {(insights.progression || []).map((p) => (
                      <div key={p.exercise_name} className={`${FOLLOW_CARD} p-4 overflow-hidden`}>
                        <div className="flex justify-between items-start gap-2">
                          <span className="font-semibold text-stone-900">{p.exercise_name}</span>
                          <span className={`text-sm font-bold ${
                            p.trend === 'up' ? 'text-lime-700' : p.trend === 'down' ? 'text-red-600' : 'text-stone-500'
                          }`}
                          >
                            {p.trend === 'up' ? '↑' : p.trend === 'down' ? '↓' : '→'}
                            {' '}
                            {p.delta_kg > 0 ? '+' : ''}
                            {p.delta_kg} kg
                          </span>
                        </div>
                        <p className="text-xs text-stone-500 mt-1">
                          {p.first_weight} kg → {p.last_weight} kg ({p.points.length} registros)
                          {(() => {
                            const last = p.points[p.points.length - 1];
                            const bits = [];
                            if (last?.reps_avg != null) bits.push(`${last.reps_avg} reps`);
                            if (last?.intensity != null) bits.push(`RIR/RPE ${last.intensity}`);
                            return bits.length ? ` · último: ${bits.join(' · ')}` : '';
                          })()}
                        </p>
                        <ProgressionBars points={p.points} exerciseName={p.exercise_name} />
                      </div>
                    ))}
                  </div>
                )}

                {tab === 'bienestar' && (
                  <div className={`${FOLLOW_CARD} p-4`}>
                    {insights.wellness ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <WellnessKpi label="Sueño (h)" value={insights.wellness.sleep_hours} />
                        <WellnessKpi label="Calidad sueño" value={insights.wellness.sleep_quality} />
                        <WellnessKpi label="Energía" value={insights.wellness.energy_level} />
                        <WellnessKpi label="Estrés" value={insights.wellness.stress_level} />
                        <WellnessKpi label="Apetito" value={insights.wellness.appetite} />
                        <WellnessKpi label="Molestias" value={insights.wellness.soreness} />
                      </div>
                    ) : (
                      <p className="text-stone-500">Sin encuestas de bienestar aún.</p>
                    )}
                  </div>
                )}

                {tab === 'deportes' && (
                  <SportCoachDashboard
                    logs={logs}
                    days={historyMode === 'all' ? 365 : (historyMode === 'range' ? 90 : 30)}
                  />
                )}

                {tab === 'sesiones' && (
                  <div className="space-y-3">
                    {!logs.length ? (
                      <p className="text-sm text-stone-500 border border-dashed border-stone-300 rounded-xl p-6 text-center">
                        No hay sesiones en {historyRange.label}.
                      </p>
                    ) : (
                      logs.map((log) => (
                        <LogCard key={log.id} log={log} readOnly={analyticsOnly || coachMode} />
                      ))
                    )}
                  </div>
                )}

                <p className="text-xs text-stone-500 mt-6 pt-4 border-t border-stone-200">
                  {analyticsOnly
                    ? 'Vista analítica. Para editar rutinas, usar Planes o IA Rutinas en el menú.'
                    : 'Para rutinas, galería e IA: menú superior o Panel Entrenadores.'}
                </p>
              </>
            )}
          </section>
        </div>
      )}

      {!coachMode && (
        <div className="space-y-4 training-light-island surface-readable rounded-xl border border-slate-200 p-4">
          {loading && <p className="text-stone-600">Cargando…</p>}
          {error && <p className="training-alert--error text-sm">{error}</p>}
          {logs.map((log) => (
            <LogCard key={log.id} log={log} readOnly />
          ))}
        </div>
      )}
    </div>
  );
}

function LogCard({ log, readOnly = true }) {
  return (
    <div className={`${FOLLOW_CARD} p-4`}>
      <div className="flex justify-between flex-wrap gap-2">
        <span className="font-bold">{log.user_name || 'Yo'}</span>
        <span className="text-sm text-stone-500">{log.fecha}</span>
      </div>
      <p className="text-sm mt-1">
        Día {log.dia} · {log.completado ? 'Completado' : (log.session_started_at && !log.session_ended_at ? 'En progreso' : 'Registrado')}
        {log.duration_minutes ? ` · ${log.duration_minutes} min` : ''}
      </p>
      {(log.ejercicios || []).length > 0 && (
        <ul className="text-xs text-stone-600 mt-2 space-y-0.5">
          {log.ejercicios.map((ex, i) => (
            <li key={i}>
              {ex.exercise_name}: {ex.sets_done}×{ex.reps_done}
              {ex.weight_kg ? ` @ ${ex.weight_kg}kg` : ''}
            </li>
          ))}
        </ul>
      )}
      {!readOnly && log.trainer_notes && (
        <p className="text-xs text-stone-500 mt-2 italic">{log.trainer_notes}</p>
      )}
    </div>
  );
}

function Kpi({ label, value, hint = null }) {
  return (
    <div className={`${FOLLOW_CARD} p-4 overflow-hidden min-h-[5.5rem]`}>
      <span className="text-xs font-bold text-stone-500 uppercase tracking-wide">{label}</span>
      <p className="text-2xl font-black text-stone-900 mt-1 leading-tight">{value}</p>
      {hint ? <p className="text-[10px] text-stone-500 mt-1">{hint}</p> : null}
    </div>
  );
}

function WellnessKpi({ label, value }) {
  return (
    <div className="bg-stone-50 rounded-lg p-3 border border-stone-100">
      <span className="text-xs text-stone-500 block">{label}</span>
      <span className="text-lg font-bold">{value ?? '—'}</span>
    </div>
  );
}
